/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { useEffect, useState } from 'react';
import { Alert, Button, Modal, ModalHeader, ModalBody, ModalFooter, Row, Col } from 'reactstrap';
import {
  useTranslator,
  useAppContext,
  callNativeMobileApp,
  isToday,
  InputSelectControlled as Select,
} from '@front-activerse/common-components';
import { useOutletContext } from 'react-router-dom';

const HydrationDayGoal = ({
  date,
  value,
  unit,
  goal,
  changeGoal,
  messages,
  hydrationSaveSettingsData,
}) => {
  const [localGoal, setLocalGoal] = useState(goal);
  const [canSaveGoal, setCanSaveGoal] = useState(false);
  const [showModalGoalReached, setShowModalGoalReached] = useState(false);
  const [goalReached, setGoalReached] = useState(value >= goal ? true : false);

  const { t } = useTranslator();
  const { trackFirebaseEvent } = useOutletContext();

  const {
    images: { hydrationSuccessIconLarge },
  } = useAppContext();

  const mililiterOptions = {
    min: 1500,
    step: 100,
    max: 3500,
  };

  const centyliterOptions = {
    min: 150,
    step: 10,
    max: 350,
  };

  const ounceOptions = {
    min: 50,
    step: 5,
    max: 100,
  };

  useEffect(() => {
    if (value >= goal) {
      if (!goalReached) {
        setShowModalGoalReached(true);
      }
      setGoalReached(true);
    } else {
      setShowModalGoalReached(false);
      setGoalReached(false);
    }
    setLocalGoal(goal);
  }, [goal, value, unit]);

  const getOptions = () => {
    if (unit === 'ml') {
      return mililiterOptions;
    } else if (unit === 'cl') {
      return centyliterOptions;
    } else if (unit === 'oz') {
      return ounceOptions;
    } else {
      throw new Error(`Unsupported unit: ${unit}`);
    }
  };

  const renderMessages = () => {
    return messages.map((msg) => (
      <Alert key={msg} color="danger" fade={false}>
        {msg}
      </Alert>
    ));
  };

  useEffect(() => {
    // hack to prevent saving goal on first render
    if (canSaveGoal) {
      saveGoal();
    }
  }, [localGoal, canSaveGoal]);

  const toggle2 = () => {
    setShowModalGoalReached(!showModalGoalReached);
  };

  useEffect(() => {
    if (showModalGoalReached) {
      trackFirebaseEvent('screen_view', {
        firebase_screen: 'HydrationTargetReached',
      });
    }
  }, [showModalGoalReached]);

  const handleInputChange = (event) => {
    const valInt = parseInt(event.target.value, 10);
    setLocalGoal(valInt);
    if (!canSaveGoal) {
      // hack to prevent saving goal on first render
      setCanSaveGoal(true);
    }
  };

  const saveGoal = async () => {
    await changeGoal(localGoal, unit);

    // change default daily goal in user settings
    await hydrationSaveSettingsData.save({
      defaultDailyGoal: { value: localGoal, unit },
    });

    trackFirebaseEvent('user_action', {
      action: 'tap-save_daily_target',
      location: 'DailyTargetHydration',
      daily_target_amount: localGoal,
      unit: unit,
    });

    callNativeMobileApp({
      action: 'refresh_data',
      payload: {
        module: 'hydration',
      },
    });
  };

  if (!isToday(date)) {
    return null;
  }
  const { min, step, max } = getOptions();

  const availableGoalValues = () => {
    const values = [];
    for (let i = min; i <= max; i += step) {
      values.push(i);
    }
    return values;
  };

  return (
    <>
      {renderMessages()}

      <div className="hydration-goal text-center">
        <Row className="align-items-center gx-0">
          <Col xs={5} className="text-end">
            <p className="me-2" data-testid="hydration-select-title">
              {t('hydration.dailyTarget.selectTitle')}
            </p>
          </Col>
          <Col xs={5}>
            <Select
              name="goal"
              handleChange={handleInputChange}
              value={localGoal}
              data-testid="hydration-select"
            >
              {availableGoalValues().map((value) => (
                <option value={value} key={value}>
                  {value} {unit}
                </option>
              ))}
            </Select>
          </Col>
        </Row>
      </div>

      <Modal
        className="hydration"
        isOpen={showModalGoalReached}
        toggle={toggle2}
        centered
        size="lg"
        data-testid="hydration-goal-reached-modal"
      >
        <ModalHeader />
        <ModalBody className="text-center">
          <img src={hydrationSuccessIconLarge} style={{ width: '101px' }} className="mb-l" alt="" />
          <h2 className="title-medium semi-bold">
            {t('hydration.doneNotificationPopup.headline')}
          </h2>
          <p className="text-text-variant-color mb-0" data-testid="hydration-goal-reached-modal">
            {t('hydration.doneNotificationPopup.content')}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={toggle2}>
            {t('hydration.doneNotificationPopup.button')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default HydrationDayGoal;
